import React, { useState, useEffect, useCallback } from 'react';
import { 
  BarChart, Bar, LineChart, Line, 
  XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer, Legend 
} from 'recharts';
import { 
  Activity, TrendingUp, TrendingDown, Package, Users, Building2, 
  Clock, CheckCircle, XCircle, AlertTriangle, Zap, DollarSign,
  Truck, Calendar, Info
} from 'lucide-react';

interface OperationsData {
  totals: {
    claims: number;
    orders: number;
    parts_assigned: number;
    parts_in_transit: number;
    parts_delivered: number;
    parts_cancelled: number;
    suppliers: number;
    active_suppliers: number;
    inactive_suppliers: number;
    workshops: number;
  };
  automation: {
    manual: { total: number; pct_of_total: number; delivery_pct: number; avg_days: number };
    automated: { total: number; pct_of_total: number; delivery_pct: number; avg_days: number };
    effectiveness: number;
  };
  averages: {
    parts_per_claim: number;
    suppliers_per_claim: number;
    part_price: number;
    delivery_days: number;
  };
  delivery: {
    supplier_delivery_rate: number;
    on_time_rate: number;
    auto_assign_rate: number;
    auto_quote_rate: number;
    on_time_orders: number;
    late_orders: number;
    delivered_orders: number;
  };
  supplier_buckets: Array<{ bucket: string; supplier_count: number }>;
  market_position: { above_market: number; below_market: number };
  risk: { price_anomalies: number; high_risk_suppliers: number };
  complex_claims: { total: number; by_insurer: Array<{ insurer_name: string; count: number }> };
  delivery_times: {
    avg_quote_to_delivery: number;
    median_delivery_days: number;
    p90_delivery_days: number;
    min_delivery_days: number;
    max_delivery_days: number;
  };
  supplier_cost_by_part: Array<{ part_name: string; avg_cost: number; order_count: number }>;
  cancellations: Array<{ supplier_name: string; cancellations: number; total_orders: number; cancel_rate: number }>;
  trend: Array<{ week_start: string; claims: number; orders: number; value: number; delivery_pct: number; wow_orders: number; wow_value: number }>;
}

interface OperationsPageProps {
  dateRange: { startDate: string; endDate: string };
  insurerKey?: string | number;
}

const InfoTooltip = ({ text }: { text: string }) => {
  const [show, setShow] = useState(false);
  return (
    <div className="relative inline-block ml-1">
      <Info 
        className="w-4 h-4 text-gray-400 cursor-help hover:text-gray-600" 
        onMouseEnter={() => setShow(true)}
        onMouseLeave={() => setShow(false)}
      />
      {show && (
        <div className="absolute z-50 w-64 p-2 text-xs text-gray-700 bg-white border border-gray-200 rounded-lg shadow-lg -top-2 left-6">
          {text}
        </div>
      )}
    </div>
  );
};

const KPICard = ({ title, value, subtitle, icon: Icon, color = 'blue', tooltip }: any) => {
  const colorClasses: Record<string, string> = {
    blue: 'from-blue-500 to-blue-600',
    green: 'from-green-500 to-green-600',
    orange: 'from-orange-500 to-orange-600',
    red: 'from-red-500 to-red-600',
    purple: 'from-purple-500 to-purple-600',
    cyan: 'from-cyan-500 to-cyan-600'
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center">
          <span className="text-sm text-gray-500">{title}</span>
          {tooltip && <InfoTooltip text={tooltip} />}
        </div>
        <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${colorClasses[color]} flex items-center justify-center`}>
          <Icon className="w-4 h-4 text-white" />
        </div>
      </div>
      <div className="text-2xl font-bold text-gray-900">{value}</div>
      {subtitle && <div className="text-xs text-gray-500 mt-1">{subtitle}</div>}
    </div>
  );
};

const StatBox = ({ label, value, color = 'gray', tooltip }: any) => (
  <div className="text-center p-3 bg-gray-50 rounded-lg">
    <div className="flex items-center justify-center text-xs text-gray-500 mb-1">
      {label}
      {tooltip && <InfoTooltip text={tooltip} />}
    </div>
    <div className={`text-lg font-bold text-${color}-600`}>{value}</div>
  </div>
);

const OperationsPage: React.FC<OperationsPageProps> = ({ dateRange, insurerKey }) => {
  const [data, setData] = useState<OperationsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('xnuup_token');
      let url = `/api/analytics/operations?startDate=${dateRange.startDate}&endDate=${dateRange.endDate}`;
      if (insurerKey && insurerKey !== 'all') {
        url += `&insurerKey=${insurerKey}`;
      }
      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const result = await response.json();
      if (result.success) {
        setData(result.data);
      } else {
        setError(result.error || 'Failed to load data');
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [dateRange, insurerKey]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 text-red-600 p-4 rounded-lg">
        <AlertTriangle className="w-5 h-5 inline mr-2" />
        {error}
      </div>
    );
  }

  if (!data) return null;

  const totalBucketSuppliers = data.supplier_buckets.reduce((sum, b) => sum + b.supplier_count, 0);

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Operations Dashboard</h1>
          <p className="text-sm text-gray-500">XNuuP Admin Monitoring</p>
        </div>
        <div className="text-sm text-gray-500">
          {dateRange.startDate} to {dateRange.endDate}
        </div>
      </div>

      {/* Row 1: Main Totals */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        <KPICard 
          title="Total Claims" 
          value={data.totals.claims.toLocaleString()} 
          icon={Package} 
          color="blue" 
          tooltip="Total number of insurance claims processed in the selected period"
        />
        <KPICard 
          title="Total Orders" 
          value={data.totals.orders.toLocaleString()} 
          icon={Activity} 
          color="green" 
          tooltip="Total part orders placed across all claims"
        />
        <KPICard 
          title="Parts Delivered" 
          value={data.totals.parts_delivered.toLocaleString()}
          subtitle={`${data.totals.parts_assigned.toLocaleString()} assigned / ${data.totals.parts_in_transit.toLocaleString()} in transit`}
          icon={Truck} 
          color="orange" 
          tooltip="Parts successfully delivered to workshops. Subtitle shows assigned (awaiting pickup) and in-transit counts"
        />
        <KPICard 
          title="Suppliers" 
          value={data.totals.suppliers.toLocaleString()}
          subtitle={`${data.totals.active_suppliers} active`}
          icon={Users} 
          color="purple" 
          tooltip="Total unique suppliers with orders. Active = suppliers with non-cancelled orders"
        />
        <KPICard 
          title="Workshops" 
          value={data.totals.workshops.toLocaleString()} 
          icon={Building2} 
          color="cyan" 
          tooltip="Total unique repair workshops receiving parts"
        />
        <KPICard 
          title="Avg Part Price" 
          value={`$${data.averages.part_price.toLocaleString()}`}
          icon={DollarSign} 
          color="green" 
          tooltip="Average price per part across all orders"
        />
      </div>
      {/* Row 4: Delivery Metrics */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <div className="flex items-center mb-4">
          <h2 className="text-lg font-semibold text-gray-900">Delivery Metrics</h2>
          <InfoTooltip text="Key delivery performance indicators and automation rates" />
        </div>
        <div className="grid grid-cols-3 md:grid-cols-6 gap-4">
          <div className="bg-green-50 rounded-lg p-4 text-center">
            <CheckCircle className="w-6 h-6 text-green-500 mx-auto mb-2" />
            <div className="text-xl font-bold text-green-600">{data.delivery.on_time_orders.toLocaleString()}</div>
            <div className="flex items-center justify-center text-xs text-gray-500">
              On-Time
              <InfoTooltip text="Orders delivered within 3 days" />
            </div>
          </div>
          <div className="bg-red-50 rounded-lg p-4 text-center">
            <XCircle className="w-6 h-6 text-red-500 mx-auto mb-2" />
            <div className="text-xl font-bold text-red-600">{data.delivery.late_orders.toLocaleString()}</div>
            <div className="flex items-center justify-center text-xs text-gray-500">
              Late
              <InfoTooltip text="Orders delivered after 3 days" />
            </div>
          </div>
          <div className="bg-blue-50 rounded-lg p-4 text-center">
            <Clock className="w-6 h-6 text-blue-500 mx-auto mb-2" />
            <div className="text-xl font-bold text-blue-600">{data.delivery.on_time_rate.toFixed(1)}%</div>
            <div className="flex items-center justify-center text-xs text-gray-500">
              On-Time Rate
              <InfoTooltip text="Percentage of delivered orders that arrived on time" />
            </div>
          </div>
          <div className="bg-purple-50 rounded-lg p-4 text-center">
            <Zap className="w-6 h-6 text-purple-500 mx-auto mb-2" />
            <div className="text-xl font-bold text-purple-600">{data.delivery.auto_assign_rate.toFixed(1)}%</div>
            <div className="flex items-center justify-center text-xs text-gray-500">
              Auto-Assign
              <InfoTooltip text="Percentage of orders automatically assigned to suppliers" />
            </div>
          </div>
          <div className="bg-cyan-50 rounded-lg p-4 text-center">
            <Truck className="w-6 h-6 text-cyan-500 mx-auto mb-2" />
            <div className="text-xl font-bold text-cyan-600">{data.delivery.delivered_orders.toLocaleString()}</div>
            <div className="flex items-center justify-center text-xs text-gray-500">
              Delivered
              <InfoTooltip text="Total orders successfully delivered" />
            </div>
          </div>
          <div className="bg-orange-50 rounded-lg p-4 text-center">
            <Activity className="w-6 h-6 text-orange-500 mx-auto mb-2" />
            <div className="text-xl font-bold text-orange-600">{data.delivery.supplier_delivery_rate.toFixed(1)}%</div>
            <div className="flex items-center justify-center text-xs text-gray-500">
              Delivery Rate
              <InfoTooltip text="Overall percentage of orders delivered" />
            </div>
          </div>
        </div>
      </div>
      
      {/* Row 3: Parts Status Table + Automation Impact */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Parts Status Table */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Parts Status Distribution</h2>
            <InfoTooltip text="Breakdown of all parts by their current fulfillment status" />
          </div>
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left py-2 text-sm font-medium text-gray-500">Status</th>
                <th className="text-right py-2 text-sm font-medium text-gray-500">Count</th>
                <th className="text-right py-2 text-sm font-medium text-gray-500">%</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b border-gray-100">
                <td className="py-3 flex items-center gap-2">
                  <span className="w-3 h-3 rounded-full bg-amber-500"></span>
                  <span className="text-sm">Assigned</span>
                  <InfoTooltip text="Parts assigned to supplier, awaiting pickup" />
                </td>
                <td className="py-3 text-right font-medium">{data.totals.parts_assigned.toLocaleString()}</td>
                <td className="py-3 text-right text-gray-500">{((data.totals.parts_assigned / data.totals.orders) * 100).toFixed(1)}%</td>
              </tr>
              <tr className="border-b border-gray-100">
                <td className="py-3 flex items-center gap-2">
                  <span className="w-3 h-3 rounded-full bg-blue-500"></span>
                  <span className="text-sm">In Transit</span>
                  <InfoTooltip text="Parts picked up and on the way to workshop" />
                </td>
                <td className="py-3 text-right font-medium">{data.totals.parts_in_transit.toLocaleString()}</td>
                <td className="py-3 text-right text-gray-500">{((data.totals.parts_in_transit / data.totals.orders) * 100).toFixed(1)}%</td>
              </tr>
              <tr className="border-b border-gray-100">
                <td className="py-3 flex items-center gap-2">
                  <span className="w-3 h-3 rounded-full bg-green-500"></span>
                  <span className="text-sm">Delivered</span>
                  <InfoTooltip text="Parts successfully delivered to workshop" />
                </td>
                <td className="py-3 text-right font-medium text-green-600">{data.totals.parts_delivered.toLocaleString()}</td>
                <td className="py-3 text-right text-green-600">{((data.totals.parts_delivered / data.totals.orders) * 100).toFixed(1)}%</td>
              </tr>
              <tr>
                <td className="py-3 flex items-center gap-2">
                  <span className="w-3 h-3 rounded-full bg-red-500"></span>
                  <span className="text-sm">Cancelled</span>
                  <InfoTooltip text="Orders cancelled before delivery" />
                </td>
                <td className="py-3 text-right font-medium text-red-600">{data.totals.parts_cancelled.toLocaleString()}</td>
                <td className="py-3 text-right text-red-600">{((data.totals.parts_cancelled / data.totals.orders) * 100).toFixed(1)}%</td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Automation Impact */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 lg:col-span-2">
          <div className="flex items-center mb-4">
            <Zap className="w-5 h-5 text-yellow-500 mr-2" />
            <h2 className="text-lg font-semibold text-gray-900">Automation Impact</h2>
            <InfoTooltip text="Comparison of manual vs automated order processing performance" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="border border-gray-200 rounded-lg p-4">
              <div className="text-sm font-medium text-gray-700 mb-3">Manual Processing</div>
              <div className="grid grid-cols-2 gap-3">
                <StatBox label="Total" value={data.automation.manual.total.toLocaleString()} color="gray" tooltip="Orders processed manually by operators" />
                <StatBox label="% of Total" value={`${data.automation.manual.pct_of_total}%`} color="gray" tooltip="Percentage of all orders processed manually" />
                <StatBox label="Delivery %" value={`${data.automation.manual.delivery_pct}%`} color="orange" tooltip="Delivery success rate for manual orders" />
                <StatBox label="Avg Days" value={data.automation.manual.avg_days.toFixed(1)} color="gray" tooltip="Average days from order to delivery" />
              </div>
            </div>
            <div className="border border-blue-200 rounded-lg p-4 bg-blue-50">
              <div className="text-sm font-medium text-blue-700 mb-3">Automated Processing</div>
              <div className="grid grid-cols-2 gap-3">
                <StatBox label="Total" value={data.automation.automated.total.toLocaleString()} color="blue" tooltip="Orders processed automatically by the system" />
                <StatBox label="% of Total" value={`${data.automation.automated.pct_of_total}%`} color="blue" tooltip="Percentage of all orders processed automatically" />
                <StatBox label="Delivery %" value={`${data.automation.automated.delivery_pct}%`} color="green" tooltip="Delivery success rate for automated orders" />
                <StatBox label="Avg Days" value={data.automation.automated.avg_days.toFixed(1)} color="blue" tooltip="Average days from order to delivery" />
              </div>
            </div>
            <div className="flex flex-col items-center justify-center border border-green-200 rounded-lg p-4 bg-green-50">
              <div className="flex items-center text-sm font-medium text-green-700 mb-2">
                Effectiveness
                <InfoTooltip text="Percentage improvement: automation vs manual delivery rate. Positive = automation performs better" />
              </div>
              <div className={`text-4xl font-bold ${data.automation.effectiveness >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {data.automation.effectiveness >= 0 ? '+' : ''}{data.automation.effectiveness}%
              </div>
              <div className="text-xs text-gray-500 mt-2">vs Manual Delivery Rate</div>
            </div>
          </div>
        </div>
      </div>

      {/* Row 4: Performance Averages + Delivery Times Statistics */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Performance Averages */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Performance Averages</h2>
            <InfoTooltip text="Key performance metrics averaged across all operations" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-gray-50 rounded-lg p-4 text-center">
              <div className="flex items-center justify-center mb-1">
                <span className="text-sm text-gray-500">Avg Parts/Claim</span>
                <InfoTooltip text="Average number of parts ordered per claim" />
              </div>
              <div className="text-2xl font-bold text-blue-600">{data.averages.parts_per_claim.toFixed(1)}</div>
            </div>
            <div className="bg-gray-50 rounded-lg p-4 text-center">
              <div className="flex items-center justify-center mb-1">
                <span className="text-sm text-gray-500">Avg Suppliers/Claim</span>
                <InfoTooltip text="Average number of different suppliers used per claim" />
              </div>
              <div className="text-2xl font-bold text-purple-600">{data.averages.suppliers_per_claim.toFixed(1)}</div>
            </div>
            <div className="bg-gray-50 rounded-lg p-4 text-center">
              <div className="flex items-center justify-center mb-1">
                <span className="text-sm text-gray-500">Delivery Rate</span>
                <InfoTooltip text="Percentage of orders successfully delivered" />
              </div>
              <div className="text-2xl font-bold text-green-600">{data.delivery.supplier_delivery_rate.toFixed(1)}%</div>
            </div>
            <div className="bg-gray-50 rounded-lg p-4 text-center">
              <div className="flex items-center justify-center mb-1">
                <span className="text-sm text-gray-500">Avg Delivery Days</span>
                <InfoTooltip text="Average number of days from order to delivery" />
              </div>
              <div className="text-2xl font-bold text-orange-600">{data.averages.delivery_days.toFixed(1)}</div>
            </div>
          </div>
        </div>

        {/* Delivery Times Statistics */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center mb-4">
            <Calendar className="w-5 h-5 text-blue-500 mr-2" />
            <h2 className="text-lg font-semibold text-gray-900">Delivery Times Statistics</h2>
            <InfoTooltip text="Statistical analysis of delivery time distribution" />
          </div>
          <div className="grid grid-cols-5 gap-3">
            <div className="bg-blue-50 rounded-lg p-3 text-center">
              <div className="flex items-center justify-center mb-1">
                <span className="text-xs text-gray-500">Avg</span>
                <InfoTooltip text="Mean delivery time across all delivered orders" />
              </div>
              <div className="text-xl font-bold text-blue-600">{data.delivery_times.avg_quote_to_delivery?.toFixed(1) || '-'}</div>
              <div className="text-xs text-gray-400">days</div>
            </div>
            <div className="bg-green-50 rounded-lg p-3 text-center">
              <div className="flex items-center justify-center mb-1">
                <span className="text-xs text-gray-500">Median</span>
                <InfoTooltip text="Middle value - 50% of deliveries are faster than this" />
              </div>
              <div className="text-xl font-bold text-green-600">{data.delivery_times.median_delivery_days?.toFixed(0) || '-'}</div>
              <div className="text-xs text-gray-400">days</div>
            </div>
            <div className="bg-orange-50 rounded-lg p-3 text-center">
              <div className="flex items-center justify-center mb-1">
                <span className="text-xs text-gray-500">P90</span>
                <InfoTooltip text="90th percentile - 90% of deliveries complete within this many days" />
              </div>
              <div className="text-xl font-bold text-orange-600">{data.delivery_times.p90_delivery_days?.toFixed(0) || '-'}</div>
              <div className="text-xs text-gray-400">days</div>
            </div>
            <div className="bg-cyan-50 rounded-lg p-3 text-center">
              <div className="flex items-center justify-center mb-1">
                <span className="text-xs text-gray-500">Min</span>
                <InfoTooltip text="Fastest delivery time recorded" />
              </div>
              <div className="text-xl font-bold text-cyan-600">{data.delivery_times.min_delivery_days || '-'}</div>
              <div className="text-xs text-gray-400">days</div>
            </div>
            <div className="bg-red-50 rounded-lg p-3 text-center">
              <div className="flex items-center justify-center mb-1">
                <span className="text-xs text-gray-500">Max</span>
                <InfoTooltip text="Slowest delivery time recorded" />
              </div>
              <div className="text-xl font-bold text-red-600">{data.delivery_times.max_delivery_days || '-'}</div>
              <div className="text-xs text-gray-400">days</div>
            </div>
          </div>
        </div>
      </div>

      {/* Row 5: Supplier Ranking Card + Market Position Card + Risk Indicators */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Supplier Ranking - Simple Card */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Supplier Ranking</h2>
            <InfoTooltip text="Distribution of suppliers by delivery success rate. Minimum 5 orders required" />
          </div>
          <div className="space-y-3">
            {data.supplier_buckets.map((bucket, idx) => {
              const pct = totalBucketSuppliers > 0 ? ((bucket.supplier_count / totalBucketSuppliers) * 100).toFixed(1) : '0';
              const colors = ['bg-green-100 text-green-700', 'bg-emerald-100 text-emerald-700', 'bg-yellow-100 text-yellow-700', 'bg-orange-100 text-orange-700', 'bg-red-100 text-red-700', 'bg-gray-100 text-gray-700'];
              return (
                <div key={idx} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                  <span className={`px-2 py-1 rounded text-sm font-medium ${colors[idx] || colors[5]}`}>
                    {bucket.bucket}
                  </span>
                  <div className="text-right">
                    <span className="font-bold text-gray-900">{bucket.supplier_count}</span>
                    <span className="text-gray-500 text-sm ml-2">({pct}%)</span>
                  </div>
                </div>
              );
            })}
            <div className="pt-2 border-t border-gray-200">
              <div className="flex items-center justify-between text-sm text-gray-600">
                <span>Total Ranked</span>
                <span className="font-bold">{totalBucketSuppliers}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Market Position - Simple Card */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Market Position</h2>
            <InfoTooltip text="Supplier pricing vs market average. Minimum 5 orders required" />
          </div>
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-red-50 rounded-lg">
              <div className="flex items-center gap-3">
                <TrendingUp className="w-6 h-6 text-red-500" />
                <div>
                  <span className="text-sm font-medium text-gray-700">Above Market</span>
                  <InfoTooltip text="Suppliers priced higher than average" />
                </div>
              </div>
              <span className="text-2xl font-bold text-red-600">{data.market_position.above_market}</span>
            </div>
            <div className="flex items-center justify-between p-4 bg-green-50 rounded-lg">
              <div className="flex items-center gap-3">
                <TrendingDown className="w-6 h-6 text-green-500" />
                <div>
                  <span className="text-sm font-medium text-gray-700">Below Market</span>
                  <InfoTooltip text="Suppliers priced at or below average" />
                </div>
              </div>
              <span className="text-2xl font-bold text-green-600">{data.market_position.below_market}</span>
            </div>
            <div className="pt-2 border-t border-gray-200">
              <div className="flex items-center justify-between text-sm text-gray-600">
                <span>Total Analyzed</span>
                <span className="font-bold">{data.market_position.above_market + data.market_position.below_market}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Risk Indicators */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Risk Indicators</h2>
            <InfoTooltip text="Potential issues requiring attention" />
          </div>
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-yellow-50 rounded-lg">
              <div className="flex items-center gap-3">
                <AlertTriangle className="w-6 h-6 text-yellow-500" />
                <div>
                  <span className="text-sm font-medium text-gray-700">Price Anomalies</span>
                  <InfoTooltip text="Parts priced >2 std deviations from average" />
                </div>
              </div>
              <span className="text-2xl font-bold text-yellow-600">{data.risk.price_anomalies}</span>
            </div>
            <div className="flex items-center justify-between p-4 bg-red-50 rounded-lg">
              <div className="flex items-center gap-3">
                <AlertTriangle className="w-6 h-6 text-red-500" />
                <div>
                  <span className="text-sm font-medium text-gray-700">High Risk Suppliers</span>
                  <InfoTooltip text="Suppliers with <50% delivery rate (min 5 orders)" />
                </div>
              </div>
              <span className="text-2xl font-bold text-red-600">{data.risk.high_risk_suppliers}</span>
            </div>
            <div className="flex items-center justify-between p-4 bg-purple-50 rounded-lg">
              <div className="flex items-center gap-3">
                <Package className="w-6 h-6 text-purple-500" />
                <div>
                  <span className="text-sm font-medium text-gray-700">Complex Claims</span>
                  <InfoTooltip text="Claims with 5+ parts requiring more coordination" />
                </div>
              </div>
              <span className="text-2xl font-bold text-purple-600">{data.complex_claims.total}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Row 6: Cost by Part & Cancellations */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Supplier Cost by Part */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Avg Cost by Part</h2>
            <InfoTooltip text="Most expensive parts by average price. Minimum 10 orders required" />
          </div>
          {data.supplier_cost_by_part.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={data.supplier_cost_by_part.slice(0, 10)} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" tickFormatter={(v) => `$${v.toLocaleString()}`} />
                <YAxis dataKey="part_name" type="category" width={150} tick={{ fontSize: 9 }} />
                <RechartsTooltip formatter={(v: number) => `$${v.toLocaleString()}`} />
                <Bar dataKey="avg_cost" fill="#10b981" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="text-gray-500 text-center py-8">No data available</div>
          )}
        </div>

        {/* Supplier Cancellations */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Supplier Cancellations</h2>
            <InfoTooltip text="Suppliers with highest cancellation rates. Minimum 10 orders required" />
          </div>
          {data.cancellations.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-2 px-2 font-medium text-gray-500">Supplier</th>
                    <th className="text-right py-2 px-2 font-medium text-gray-500">Cancelled</th>
                    <th className="text-right py-2 px-2 font-medium text-gray-500">Total</th>
                    <th className="text-right py-2 px-2 font-medium text-gray-500">Rate</th>
                  </tr>
                </thead>
                <tbody>
                  {data.cancellations.slice(0, 10).map((row, i) => (
                    <tr key={i} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-2 px-2 truncate max-w-[150px]">{row.supplier_name}</td>
                      <td className="py-2 px-2 text-right text-red-600 font-medium">{row.cancellations}</td>
                      <td className="py-2 px-2 text-right text-gray-600">{row.total_orders}</td>
                      <td className="py-2 px-2 text-right">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          row.cancel_rate > 20 ? 'bg-red-100 text-red-700' : 
                          row.cancel_rate > 10 ? 'bg-yellow-100 text-yellow-700' : 'bg-gray-100 text-gray-700'
                        }`}>
                          {row.cancel_rate.toFixed(1)}%
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-gray-500 text-center py-8">No cancellation data available</div>
          )}
        </div>
      </div>

      {/* Row 7: Operation Trend */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <div className="flex items-center mb-4">
          <TrendingUp className="w-5 h-5 text-green-500 mr-2" />
          <h2 className="text-lg font-semibold text-gray-900">Operation Trend (Weekly)</h2>
          <InfoTooltip text="Weekly trends showing claims volume, order count, and delivery percentage" />
        </div>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={data.trend}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis 
              dataKey="week_start" 
              tick={{ fontSize: 11 }}
              tickFormatter={(v) => new Date(v).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
            />
            <YAxis yAxisId="left" />
            <YAxis yAxisId="right" orientation="right" domain={[0, 100]} />
            <RechartsTooltip 
              labelFormatter={(v) => new Date(v).toLocaleDateString()}
              formatter={(v: number, name: string) => [
                name === 'value' ? `$${v.toLocaleString()}` : 
                name === 'delivery_pct' ? `${v}%` : v.toLocaleString(),
                name
              ]}
            />
            <Legend />
            <Line yAxisId="left" type="monotone" dataKey="claims" stroke="#3a7de4" strokeWidth={2} name="Claims" />
            <Line yAxisId="left" type="monotone" dataKey="orders" stroke="#10b981" strokeWidth={2} name="Orders" />
            <Line yAxisId="right" type="monotone" dataKey="delivery_pct" stroke="#f59e0b" strokeWidth={2} name="Delivery %" />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Row 8: Complex Claims by Insurer */}
      {data.complex_claims.by_insurer.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Complex Claims by Insurer</h2>
            <InfoTooltip text="Distribution of complex claims (5+ parts) across insurance companies" />
          </div>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={data.complex_claims.by_insurer}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="insurer_name" tick={{ fontSize: 10 }} />
              <YAxis />
              <RechartsTooltip />
              <Bar dataKey="count" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
};

export default OperationsPage;